import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import { ApiError } from "./types/common-types";
import {
  ElectricProviderState,
  ElectricProviderResponse,
  ElectricProvider,
} from "./types/electric-types";
import { PaginationReturn } from "./types/types";

const initialState: ElectricProviderState = {
  electricProviders: [],
  allElectricProviders: [],
  electricProvider: null,
  selectedElectricProvider: null,
  loading: true,
  allLoading: false,
  error: null,
  allError: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// CREATE DataProvider
export const createElectricProvider = createAsyncThunk<
  ElectricProviderResponse,
  FormData | Omit<ElectricProvider, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("electricProviders/create", async (electricProviderData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = electricProviderData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/electric-providers`,
      electricProviderData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return {
      message: res.data.message,
      electricProvider: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create data provider" },
    );
  }
});

// READ (fetch all) ElectricProviders
export const fetchElectricProviders = createAsyncThunk<
  ElectricProviderResponse & PaginationReturn,
  void | { url: string } | any,
  { rejectValue: ApiError; state: RootState }
>("electricProviders/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url: string | undefined = urlFilters?.url;
    const apiUrl = url || `${API_URL}/electric-providers`;
    if (urlFilters?.url) {
      urlFilters.url = "";
    }
    const res = await axios.get(apiUrl, {
      params: urlFilters,
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      message: res.data.data.message,
      electricProviders: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch electric providers" },
    );
  }
});

// READ (fetch all providers without pagination)
export const fetchAllElectricProviders = createAsyncThunk<
  ElectricProvider[],
  void,
  { rejectValue: ApiError; state: RootState }
>("electricProviders/fetchAllList", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url = new URL(`${API_URL}/electric-providers`);
    url.searchParams.set("per_page", "1000");
    const res = await axios.get(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload = res.data?.data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch electric providers" },
    );
  }
});

// READ (fetch one) DataProvider by ID
export const fetchElectricProviderById = createAsyncThunk<
  ElectricProvider,
  number,
  { rejectValue: ApiError; state: RootState }
>("electricProviders/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/electric-providers${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch electric provider" },
    );
  }
});

// UPDATE DataProvider
export const updateElectricProvider = createAsyncThunk<
  ElectricProvider,
  { id: number; data: FormData | Partial<ElectricProvider> },
  { rejectValue: ApiError; state: RootState }
>("electricProviders/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/electric-providers/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update electric provider" },
    );
  }
});

// DELETE DataProvider
export const deleteElectricProvider = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("electricProviders/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/electric-providers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete electric provider" },
    );
  }
});

// Slice
const electricProviderSlice = createSlice({
  name: "electricProviders",
  initialState,
  reducers: {
    resetElectricProviderState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedElectricProvider = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createElectricProvider.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createElectricProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.electricProvider) {
          state.electricProviders.unshift(action.payload.electricProvider);
        }
      })
      .addCase(createElectricProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create electric provider";
      })

      // Fetch All
      .addCase(fetchElectricProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchElectricProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.electricProviders = action.payload.electricProviders || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchElectricProviders.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch electric providers";
      })
      .addCase(fetchAllElectricProviders.pending, (state) => {
        state.allLoading = true;
        state.allError = null;
      })
      .addCase(fetchAllElectricProviders.fulfilled, (state, action) => {
        state.allLoading = false;
        state.allElectricProviders = action.payload || [];
      })
      .addCase(fetchAllElectricProviders.rejected, (state, action) => {
        state.allLoading = false;
        state.allError =
          action.payload?.message || "Failed to fetch electric providers";
      })

      // Fetch One
      .addCase(fetchElectricProviderById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedElectricProvider = null;
      })
      .addCase(fetchElectricProviderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedElectricProvider = action.payload;
      })
      .addCase(fetchElectricProviderById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch electric provider";
      })

      // Update
      .addCase(updateElectricProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateElectricProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.electricProviders = state.electricProviders.map((provider) =>
          provider.id === action.payload.id ? action.payload : provider,
        );
        if (state.selectedElectricProvider?.id === action.payload.id) {
          state.selectedElectricProvider = action.payload;
        }
      })
      .addCase(updateElectricProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update electric provider";
      })

      // Delete
      .addCase(deleteElectricProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteElectricProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.electricProviders = state.electricProviders.filter(
          (provider) => provider.id !== action.payload,
        );
      })
      .addCase(deleteElectricProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete electric provider";
      });
  },
});

export const { resetElectricProviderState } = electricProviderSlice.actions;
export default electricProviderSlice.reducer;
